/**
 * An inspection, filled in: the short daily round or the detailed inspection,
 * for one facility and one trade. Every item starts OK; the inspector marks
 * what is not, says why, and names the repair it needs. Saving keeps the
 * record; the defects then wait on the facility until a work order is raised
 * for each — here, straight after saving, or later from the findings list.
 */
import { useMemo, useState } from "react";
import { Button, Card, Field, Input, Select } from "../../design/components";
import {
  FORM_LABEL,
  RESULTS,
  RESULT_LABEL,
  TRADES,
  contractorFor,
  defectsOf,
  isDefect,
  newInspectionId,
  saveInspection,
  scoreOf,
  bandOf,
  BAND_LABEL,
  tradeOf,
  type Fault,
  type FormKind,
  type InspectionRecord,
  type InspectionStore,
  type Result,
  type TradeId,
} from "../../domain/assetInspections";
import { FACILITY_LIST } from "../../domain/facilityRegister";
import { t, tn } from "../../i18n";
import { useWorkOrderActions } from "./facilityData";
import { itemText } from "./labels";
import s from "./FacilityInspections.module.css";

export interface FormStart {
  facilityId: string;
  trade: TradeId;
  form: FormKind;
}

const localInput = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);

export function InspectionForm({
  start,
  store,
  who,
  onSave,
  onClose,
}: {
  start: FormStart;
  store: InspectionStore;
  who: string;
  onSave: (next: InspectionStore) => Promise<void>;
  onClose: () => void;
}) {
  const [facilityId, setFacilityId] = useState(start.facilityId);
  const [trade, setTrade] = useState<TradeId>(start.trade);
  const [form, setForm] = useState<FormKind>(start.form);
  const [at, setAt] = useState(() => localInput(new Date()));
  const [inspector, setInspector] = useState(who);
  const [company, setCompany] = useState(() => contractorFor(store, start.trade).company);
  const [marks, setMarks] = useState<Record<string, Fault>>({});
  const [remarks, setRemarks] = useState("");
  const [saved, setSaved] = useState<InspectionRecord | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { raise } = useWorkOrderActions();

  const tr = tradeOf(trade);
  const sections = useMemo(
    () => (form === "daily" ? [{ id: "daily", title: "", items: tr.daily }] : tr.detailed),
    [form, tr],
  );

  const setResult = (itemId: string, result: Result) =>
    setMarks((m) => {
      const next = { ...m };
      if (result === "ok") delete next[itemId];
      else next[itemId] = { ...m[itemId], itemId, result };
      return next;
    });
  const setField = (itemId: string, patch: Partial<Fault>) => setMarks((m) => ({ ...m, [itemId]: { ...m[itemId], ...patch } }));

  const draft: InspectionRecord = {
    id: saved?.id ?? "draft",
    facilityId,
    trade,
    form,
    at: new Date(at).toISOString(),
    inspector: inspector.trim(),
    company: company.trim(),
    faults: Object.values(marks),
    remarks: remarks.trim() || undefined,
  };
  const score = scoreOf(draft);
  const defects = defectsOf(draft);

  const save = async () => {
    if (!draft.inspector || !draft.company) {
      setError(t("Name the inspector and the company before saving."));
      return;
    }
    setBusy(true);
    setError(null);
    const rec = { ...draft, id: newInspectionId() };
    try {
      await onSave(saveInspection(store, rec));
      setSaved(rec);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /* after saving: a work order for each defect, one press each or all at once */
  const [raised, setRaised] = useState<Record<string, string>>({});
  const raiseOne = async (current: InspectionStore, fault: Fault): Promise<InspectionStore> => {
    const { store: next, order } = await raise(current, saved!, fault);
    setRaised((r) => ({ ...r, [fault.itemId]: order.number }));
    return next;
  };
  const raiseAll = async () => {
    setBusy(true);
    setError(null);
    try {
      let current = store;
      for (const f of defectsOf(saved!)) if (!raised[f.itemId]) current = await raiseOne(current, f);
      await onSave(current);
    } catch (e) {
      setError(t("A work order was not raised: {why}", { why: e instanceof Error ? e.message : String(e) }));
    } finally {
      setBusy(false);
    }
  };
  const raiseSingle = async (f: Fault) => {
    setBusy(true);
    setError(null);
    try {
      await onSave(await raiseOne(store, f));
    } catch (e) {
      setError(t("A work order was not raised: {why}", { why: e instanceof Error ? e.message : String(e) }));
    } finally {
      setBusy(false);
    }
  };

  if (saved) {
    const found = defectsOf(saved);
    return (
      <Card title={t("Inspection saved")} sub={`${saved.id} · ${saved.facilityId} · ${t(tr.label)} · ${t(FORM_LABEL[saved.form])}`}>
        <div className={s.form} data-testid="inspection-saved">
          <p className={s.ok}>
            {t("Condition {score} — {band}.", { score, band: t(BAND_LABEL[bandOf(score)]) })}{" "}
            {found.length === 0 ? t("Nothing found to repair.") : tn(found.length, "{n} defect found.", "{n} defects found.")}
          </p>
          {found.length > 0 && (
            <>
              <ul className={s.list}>
                {found.map((f) => (
                  <li key={f.itemId} className={s.finding}>
                    <span className={`${s.sev} ${s[`sev_${f.result}`]}`}>{t(RESULT_LABEL[f.result])}</span>
                    <span>{itemText(tr, f.itemId)}</span>
                    {f.repairType && <span className={s.muted}>· {t(f.repairType)}</span>}
                    {raised[f.itemId] ? (
                      <span className={s.ok}>{t("Work order {number}", { number: raised[f.itemId] })}</span>
                    ) : (
                      <Button size="sm" onClick={() => void raiseSingle(f)} disabled={busy}>
                        {t("New Work Order")}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
              <div className={s.bar}>
                <Button variant="primary" onClick={() => void raiseAll()} disabled={busy || found.every((f) => raised[f.itemId])}>
                  {busy ? t("Creating…") : t("New Work Orders for every defect")}
                </Button>
                <span className={s.muted}>{t("They join the work-orders register that Operations control works from.")}</span>
              </div>
            </>
          )}
          {error && <p className={s.err}>{error}</p>}
          <div className={s.bar}>
            <Button onClick={onClose}>{t("Done")}</Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card title={t(FORM_LABEL[form])} sub={t("Every item starts OK — mark only what is not.")}>
      <div className={s.form} data-testid="inspection-form">
        <div className={s.formHead}>
          <Field label="Facility">
            <Select value={facilityId} onChange={(e) => setFacilityId(e.target.value)}>
              {FACILITY_LIST.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.id} · {f.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Trade">
            <Select
              value={trade}
              onChange={(e) => {
                const next = e.target.value as TradeId;
                setTrade(next);
                setMarks({});
                setCompany(contractorFor(store, next).company);
              }}
            >
              {TRADES.map((x) => (
                <option key={x.id} value={x.id}>
                  {t(x.label)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Form">
            <Select
              value={form}
              onChange={(e) => {
                setForm(e.target.value as FormKind);
                setMarks({});
              }}
            >
              <option value="daily">{t("Daily inspection — short form")}</option>
              <option value="detailed">{t("Detailed inspection — full form")}</option>
            </Select>
          </Field>
          <Field label="Date and time">
            <Input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} />
          </Field>
          <Field label="Inspector">
            <Input value={inspector} onChange={(e) => setInspector(e.target.value)} />
          </Field>
          <Field label="Company">
            <Input value={company} onChange={(e) => setCompany(e.target.value)} />
          </Field>
        </div>

        <div>
          {sections.map((sec) => (
            <div key={sec.id}>
              {sec.title && <div className={s.sectionTitle}>{t(sec.title)}</div>}
              {sec.items.map((it) => {
                const m = marks[it.id];
                const r: Result = m?.result ?? "ok";
                return (
                  <div key={it.id} className={s.item} data-testid={`item-${it.id}`}>
                    <span>{t(it.text)}</span>
                    <span className={s.results} role="group" aria-label={t(it.text)}>
                      {RESULTS.map((x) => (
                        <button key={x} type="button" className={s[`r_${x}`]} aria-pressed={r === x} onClick={() => setResult(it.id, x)}>
                          {t(RESULT_LABEL[x])}
                        </button>
                      ))}
                    </span>
                    {isDefect(r) && (
                      <div className={s.itemExtra}>
                        <Input
                          placeholder={t("What was found")}
                          aria-label={t("What was found")}
                          value={m?.note ?? ""}
                          onChange={(e) => setField(it.id, { note: e.target.value })}
                        />
                        <Select
                          aria-label={t("Repair needed")}
                          value={m?.repairType ?? ""}
                          onChange={(e) => setField(it.id, { repairType: e.target.value || undefined })}
                        >
                          <option value="">{t("Repair needed…")}</option>
                          {tr.repairTypes.map((rt) => (
                            <option key={rt} value={rt}>
                              {t(rt)}
                            </option>
                          ))}
                        </Select>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <Field label="Remarks">
          <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        </Field>

        <div className={s.bar}>
          <span className={s.mono}>
            {t("Condition {score} — {band}", { score, band: t(BAND_LABEL[bandOf(score)]) })} ·{" "}
            {tn(defects.length, "{n} defect", "{n} defects")}
          </span>
          <span className={s.spacer} />
          <Button onClick={onClose} disabled={busy}>
            {t("Cancel")}
          </Button>
          <Button variant="primary" onClick={() => void save()} disabled={busy}>
            {busy ? t("Saving…") : t("Save the inspection")}
          </Button>
        </div>
        {error && <p className={s.err}>{error}</p>}
      </div>
    </Card>
  );
}
